import React from "react";
import { BadgeCheck, Copy, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import {
  ReferralPartner,
  partnerTermsLink,
  referralShareLink,
} from "../../features/admin/models/ReferralPartner";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";

const formatMoney = (minorUnits: number, currency: string, locale: string): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minorUnits / 100);

const copyToClipboard = async (value: string, doneMessage: string) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(doneMessage);
  } catch {
    toast.error("Could not copy");
  }
};

interface ReferralPartnerSummaryProps {
  partner: ReferralPartner;
  payoutThresholdMinorUnits: number;
  busy: boolean;
  onMarkPaid: () => void;
  onDelete: () => void;
}

/** Partner identity, links to share, the unpaid balance, and the settle action. */
const ReferralPartnerSummary: React.FC<ReferralPartnerSummaryProps> = ({
  partner,
  payoutThresholdMinorUnits,
  busy,
  onMarkPaid,
  onDelete,
}) => {
  const unpaid = formatMoney(partner.unpaidAmountMinorUnits, partner.bountyCurrency, getDisplayLocale());
  const threshold = formatMoney(payoutThresholdMinorUnits, partner.bountyCurrency, getDisplayLocale());
  const belowThreshold =
    partner.unpaidAmountMinorUnits > 0 &&
    partner.unpaidAmountMinorUnits < payoutThresholdMinorUnits;

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="truncate text-lg font-semibold text-grey-10 dark:text-white">
            {partner.name}
          </p>
          <p className="truncate text-sm text-grey-40 dark:text-grey-70">{partner.email}</p>
          {partner.termsAcceptedAt ? (
            <p className="mt-1 flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400">
              <BadgeCheck size={16} />
              Terms v{partner.termsVersion} accepted
            </p>
          ) : (
            <p className="mt-1 text-sm text-grey-50 dark:text-grey-60">
              Terms not accepted yet — the code does not attribute until they accept.
            </p>
          )}
          {partner.notes && (
            <p className="mt-2 text-sm text-grey-50 dark:text-grey-70">{partner.notes}</p>
          )}
        </div>
        <div className="text-right">
          <p className="text-sm text-grey-50 dark:text-grey-70">Unpaid balance</p>
          <p className="text-3xl font-bold text-grey-10 dark:text-white">{unpaid}</p>
          {belowThreshold && (
            <p className="text-xs text-grey-50 dark:text-grey-60">
              Settles at {threshold} — carries over until then
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn btn-secondary flex items-center gap-1.5"
          onClick={() =>
            copyToClipboard(referralShareLink(partner), "Share link copied")
          }
        >
          <Copy size={16} />
          Copy share link
        </button>
        <button
          type="button"
          className="btn btn-secondary flex items-center gap-1.5"
          onClick={() => copyToClipboard(partnerTermsLink(partner), "Terms link copied")}
        >
          <Copy size={16} />
          Copy terms link
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy || partner.unpaidAmountMinorUnits === 0}
          onClick={onMarkPaid}
        >
          Mark {unpaid} as paid
        </button>
        <button
          type="button"
          className="ml-auto flex items-center gap-1.5 text-sm font-medium text-red-700 hover:underline dark:text-red-400"
          disabled={busy}
          onClick={onDelete}
        >
          <Trash2 size={16} />
          Remove partner
        </button>
      </div>
    </div>
  );
};

export default ReferralPartnerSummary;
